/**
 * SoccerEasterEgg — a rare, delightful 3D ⚽ (three.js). It drops in somewhere on screen, bounces to
 * rest at the bottom, stops spinning, and waits. Click it and it gets "kicked" — spinning with a
 * slicing sidespin it grows and sails up & out over your head, then GOOOOAAAALLL flies across the
 * screen. If it was resting on the ground when kicked, a little grass kicks up too (an air-kick
 * leaves no grass — there's no ground to scuff).
 *
 * FULLY OFF (2026-07, Matt's request): EASTER_EGG_ENABLED is false, which skips the effect
 * entirely — no random spawns, and the secret console command (__goal()) isn't even registered.
 * Flip EASTER_EGG_ENABLED back to true to restore it (RANDOM_SPAWN_ENABLED separately controls
 * just the ambient random spawns vs. console-only).
 * Only ever ONE ball at a time (the phase guard). The ball persists until kicked or the page reloads.
 * three.js is imported lazily on first spawn, so it stays out of the normal app bundle.
 */

import { useEffect, useRef, useState } from 'react'

// Master on/off switch — set true to bring the ball back. While false, NEITHER trigger works: no
// random spawns and the secret console command (window.__goal()) is never even registered.
const EASTER_EGG_ENABLED = false

// Rare random appearances. At a 60s visible-tab roll, 0.2% ≈ about once a day per person
// (~0.12/hr mean). The one-at-a-time guard + the AJ-fox cross-lock keep real counts at/below that.
const RANDOM_SPAWN_ENABLED = true
const CHECK_EVERY_MS = 60 * 1000
const SPAWN_CHANCE   = 0.002  // ~0.12/hr → about once a day per person; rare on purpose

const KEYFRAMES = `
@keyframes ajGoalFly {
  0%   { transform: translate(100vw,-50%) rotate(-7deg) scale(0.9); opacity:0 }
  10%  { opacity:1 }
  34%  { transform: translate(calc(50vw - 50%),-50%) rotate(-7deg) scale(1.14); opacity:1 }
  46%  { transform: translate(calc(50vw - 50%),-50%) rotate(-7deg) scale(1.0);  opacity:1 }
  64%  { transform: translate(calc(50vw - 50%),-50%) rotate(-7deg) scale(1.0);  opacity:1 }
  100% { transform: translate(calc(-100% - 14vw),-50%) rotate(-7deg) scale(0.96); opacity:1 }
}
@keyframes ajGrass {
  0%   { transform: translate(0,0) rotate(var(--r0)) scaleY(0.25); opacity:0 }
  18%  { opacity:1 }
  100% { transform: translate(var(--dx),var(--dy)) rotate(var(--r1)) scaleY(1); opacity:0 }
}`
const GOAL_MS = 2800   // keep in sync with the GOAL animation duration below
const GRASS_MS = 750

// Build a real soccer ball: a truncated icosahedron (12 black pentagons + 20 white hexagons),
// projected onto a sphere of radius R, with the panels inset so a dark base sphere shows through as
// seams. Panels are tessellated into curved caps and every vertex normal points radially, so the
// whole ball shades like a smooth sphere (no visible facets) while keeping the authentic pattern.
function buildSoccerBall(THREE, R) {
  const PHI = (1 + Math.sqrt(5)) / 2
  const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
  const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
  const scl = (a, s) => [a[0] * s, a[1] * s, a[2] * s]
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
  const len = a => Math.hypot(a[0], a[1], a[2])
  const nrm = a => { const l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l] }
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]

  // 12 icosahedron vertices (the pentagon centres of the truncated form)
  const V = [[0, 1, PHI], [0, 1, -PHI], [0, -1, PHI], [0, -1, -PHI],
    [1, PHI, 0], [1, -PHI, 0], [-1, PHI, 0], [-1, -PHI, 0],
    [PHI, 0, 1], [PHI, 0, -1], [-PHI, 0, 1], [-PHI, 0, -1]].map(nrm)
  let minD = Infinity
  for (let i = 0; i < 12; i++) for (let j = i + 1; j < 12; j++) { const d = len(sub(V[i], V[j])); if (d < minD) minD = d }
  const thr = minD * 1.05
  const isEdge = (i, j) => i !== j && len(sub(V[i], V[j])) <= thr

  // pentagons: around each vertex, the 1/3-point along each of its 5 edges
  const pentagons = []
  for (let v = 0; v < 12; v++) {
    const pts = []
    for (let w = 0; w < 12; w++) if (isEdge(v, w)) pts.push(add(V[v], scl(sub(V[w], V[v]), 1 / 3)))
    pentagons.push(pts)
  }
  // hexagons: each icosahedron triangle → the 1/3 & 2/3 points along its three edges
  const hexagons = []
  for (let a = 0; a < 12; a++) for (let b = a + 1; b < 12; b++) for (let c = b + 1; c < 12; c++) {
    if (!(isEdge(a, b) && isEdge(b, c) && isEdge(a, c))) continue
    const pts = []
    for (const [p, q] of [[a, b], [b, c], [c, a]]) {
      pts.push(add(V[p], scl(sub(V[q], V[p]), 1 / 3)))
      pts.push(add(V[p], scl(sub(V[q], V[p]), 2 / 3)))
    }
    hexagons.push(pts)
  }

  const D = 3   // panel tessellation depth (curvature smoothness)
  function panelGeo(polys, inset) {
    const pos = [], nor = []
    const onSphere = p => scl(nrm(p), R)
    const emit = p => { const u = nrm(p); pos.push(u[0] * R, u[1] * R, u[2] * R); nor.push(u[0], u[1], u[2]) }
    for (const poly of polys) {
      const P = poly.map(onSphere)
      const C = onSphere(P.reduce((s, p) => add(s, p), [0, 0, 0]))
      const n = nrm(C)
      let e1 = sub(P[0], C); e1 = nrm(sub(e1, scl(n, dot(e1, n)))); const e2 = cross(n, e1)
      P.sort((p, q) => {
        const dp = sub(p, C), dq = sub(q, C)
        return Math.atan2(dot(dp, e2), dot(dp, e1)) - Math.atan2(dot(dq, e2), dot(dq, e1))
      })
      // inset toward centroid but keep on the sphere (tangential gap = seam)
      const Pi = P.map(p => onSphere(add(C, scl(sub(p, C), inset))))
      // fan from centroid, each fan triangle subdivided & re-projected to the sphere (curved cap)
      for (let k = 0; k < Pi.length; k++) {
        const A = Pi[k], B = Pi[(k + 1) % Pi.length]
        const bary = (i, j) => add(add(scl(C, (D - i - j) / D), scl(A, i / D)), scl(B, j / D))
        for (let i = 0; i < D; i++) for (let j = 0; j < D - i; j++) {
          emit(bary(i, j)); emit(bary(i + 1, j)); emit(bary(i, j + 1))
          if (j < D - i - 1) { emit(bary(i + 1, j)); emit(bary(i + 1, j + 1)); emit(bary(i, j + 1)) }
        }
      }
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
    g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3))
    return g
  }
  const pentaG = panelGeo(pentagons, 0.955)
  const hexaG = panelGeo(hexagons, 0.955)
  const black = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.45, metalness: 0, transparent: true })
  const white = new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.42, metalness: 0, transparent: true })
  const base = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.8, metalness: 0, transparent: true })
  const baseSphere = new THREE.Mesh(new THREE.SphereGeometry(R * 0.99, 64, 64), base)
  const group = new THREE.Group()
  group.add(baseSphere, new THREE.Mesh(pentaG, black), new THREE.Mesh(hexaG, white))
  return {
    group, materials: [black, white, base],
    dispose() { pentaG.dispose(); hexaG.dispose(); baseSphere.geometry.dispose(); black.dispose(); white.dispose(); base.dispose() },
  }
}

export default function SoccerEasterEgg() {
  const [phase, setPhase] = useState('idle')   // 'idle' | 'ball' | 'goal'
  const [grass, setGrass] = useState(null)      // { x, y, id } — grass burst at a grounded kick
  const mountRef = useRef(null)                 // three canvas host
  const hitRef = useRef(null)                   // DOM click target tracking the ball
  const ctlRef = useRef(null)                   // imperative handle to the running scene { kick }
  const grassRef = useRef(() => {})             // latest showGrass(x, y) closure
  const grassIdRef = useRef(0)
  grassRef.current = (x, y) => { grassIdRef.current += 1; setGrass({ x, y, id: grassIdRef.current }) }

  function spawn() { setPhase(p => (p === 'idle' ? 'ball' : p)) }   // never two at once

  // Publish whether the ball is on screen so AJ the fox's random walk-by can defer to it (and vice
  // versa) — the two easter eggs are mutually exclusive on random spawns.
  useEffect(() => { window.__ajSoccerActive = phase !== 'idle' }, [phase])

  // Secret console trigger + (optional) random spawner. The chance is rolled even while the tab is
  // hidden; a hit while you're away is QUEUED and the ball drops in when you return to the tab. We
  // defer the actual spawn to a visible tab on purpose — background tabs can refuse a WebGL context,
  // and it makes for a proper "welcome back" reveal. (dt is clamped in the animation loop, so a long
  // absence never makes the resumed physics jump.)
  useEffect(() => {
    if (!EASTER_EGG_ENABLED) return
    window.__goal = spawn
    let interval = null
    let pending = false
    const reveal = () => { if (pending && !window.__ajWalkerActive && document.visibilityState === 'visible') { pending = false; spawn() } }
    if (RANDOM_SPAWN_ENABLED) {
      interval = setInterval(() => {
        if (Math.random() >= SPAWN_CHANCE) return
        if (window.__ajWalkerActive) return         // AJ the fox is walking — don't overlap
        if (document.visibilityState === 'visible') spawn()
        else pending = true                         // rolled a hit while away → greet them on return
      }, CHECK_EVERY_MS)
      document.addEventListener('visibilitychange', reveal)
    }
    return () => {
      if (window.__goal === spawn) delete window.__goal
      if (interval) clearInterval(interval)
      document.removeEventListener('visibilitychange', reveal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 3D ball lifecycle (only while phase === 'ball').
  useEffect(() => {
    if (phase !== 'ball') return
    let cancelled = false, raf = 0, cleanup = () => {}

    import('three').then(THREE => {
      if (cancelled || !mountRef.current) return
      const host = mountRef.current
      const W = () => window.innerWidth, H = () => window.innerHeight
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.setClearColor(0x000000, 0)
      // anchor as a fixed, full-viewport canvas sized strictly from innerWidth/innerHeight, so no
      // ancestor box or global CSS rule can stretch it (the ball stays perfectly round)
      const cv = renderer.domElement
      cv.style.position = 'fixed'; cv.style.top = '0'; cv.style.left = '0'; cv.style.display = 'block'
      host.appendChild(cv)

      const scene = new THREE.Scene()
      // Orthographic camera: a sphere always projects to a TRUE circle regardless of where it sits.
      // (A perspective camera renders off-centre spheres as diagonal ellipses — that was the "oval".)
      const VIS_H = 9.4   // world height mapped to the viewport
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100)
      camera.position.set(0, 0, 10)
      let visH = VIS_H, visW = 0
      function setSize() {
        const w = W(), h = H()
        renderer.setSize(w, h, false); cv.style.width = w + 'px'; cv.style.height = h + 'px'
        visW = VIS_H * (w / h)
        camera.left = -visW / 2; camera.right = visW / 2; camera.top = VIS_H / 2; camera.bottom = -VIS_H / 2
        camera.updateProjectionMatrix()
      }
      setSize()
      window.addEventListener('resize', setSize)

      scene.add(new THREE.AmbientLight(0xffffff, 0.7))
      const key = new THREE.DirectionalLight(0xffffff, 0.95); key.position.set(4, 8, 6); scene.add(key)
      const fill = new THREE.DirectionalLight(0xffffff, 0.35); fill.position.set(-5, -2, 4); scene.add(fill)

      const R = visH * 0.05                       // ball radius (≈ 5% of viewport height)
      const { group: ball, materials, dispose: disposeBall } = buildSoccerBall(THREE, R)
      ball.position.set((Math.random() * 0.7 - 0.35) * visW, visH / 2 - R, 0)
      scene.add(ball)

      // physics state (world units, seconds)
      let vy = 0, vx = (Math.random() * 0.6 - 0.3)
      const G = -visH * 2.4, REST = 0.62, FLOOR = -visH / 2 + R
      let resting = false
      const spinAxis = new THREE.Vector3(0.28, 1, 0.22).normalize()   // tilted axis → 3D slicing spin
      let spinSpeed = 6
      const kick = { active: false, t: 0, dur: 0.85, x0: 0, y0: 0 }

      const screenR = () => (R / (visH / 2)) * (H() / 2)
      const v = new THREE.Vector3()
      const project = () => { v.copy(ball.position).project(camera); return { sx: (v.x * 0.5 + 0.5) * W(), sy: (-v.y * 0.5 + 0.5) * H() } }

      ctlRef.current = {
        kick() {
          if (kick.active) return
          // grass only if it's actually sitting on the ground
          if (resting) { const { sx, sy } = project(); grassRef.current(sx, sy + screenR() * 0.85) }
          kick.active = true; kick.t = 0; kick.x0 = ball.position.x; kick.y0 = ball.position.y
          spinSpeed = 26                                   // fast slicing spin
          if (hitRef.current) hitRef.current.style.display = 'none'
        },
      }

      function placeHit() {
        if (!hitRef.current || kick.active) return
        const { sx, sy } = project()
        const el = hitRef.current
        el.style.width = el.style.height = (screenR() * 2.2) + 'px'
        el.style.left = sx + 'px'; el.style.top = sy + 'px'
      }

      let last = performance.now()
      function frame(now) {
        if (cancelled) return
        const dt = Math.min((now - last) / 1000, 0.05); last = now

        if (spinSpeed > 0) ball.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(spinAxis, spinSpeed * dt))

        if (kick.active) {
          kick.t += dt
          const p = Math.min(kick.t / kick.dur, 1)
          const e = 1 - Math.pow(1 - p, 2)                // ease-out
          ball.scale.setScalar(1 + e * 11)                // grows as if rushing at you (over your head)
          ball.position.y = kick.y0 + e * (visH * 0.85)   // and upward, out of frame
          ball.position.x = kick.x0 + e * (visW * 0.1)
          const op = p < 0.65 ? 1 : Math.max(0, 1 - (p - 0.65) / 0.35)
          materials.forEach(m => { m.opacity = op })
          if (p >= 1) { cleanup(); if (!cancelled) setPhase('goal'); return }
        } else if (!resting) {
          vy += G * dt
          ball.position.y += vy * dt; ball.position.x += vx * dt
          if (ball.position.x < -visW / 2 + R) { ball.position.x = -visW / 2 + R; vx = -vx * 0.7 }
          if (ball.position.x > visW / 2 - R) { ball.position.x = visW / 2 - R; vx = -vx * 0.7 }
          if (ball.position.y <= FLOOR) {
            ball.position.y = FLOOR; vy = -vy * REST; vx *= 0.78; spinSpeed *= 0.55   // slow the spin each bounce
            if (Math.abs(vy) < visH * 0.25) { vy = 0; resting = true; spinSpeed = 0 }  // settle: come to a full stop
          }
        }
        placeHit()
        renderer.render(scene, camera)
        raf = requestAnimationFrame(frame)
      }
      raf = requestAnimationFrame(frame)

      cleanup = () => {
        cancelAnimationFrame(raf)
        window.removeEventListener('resize', setSize)
        disposeBall(); renderer.dispose()
        if (cv.parentNode) cv.parentNode.removeChild(cv)
        ctlRef.current = null
      }
    }).catch(() => { if (!cancelled) setPhase('idle') })

    return () => { cancelled = true; cleanup() }
  }, [phase])

  // GOAL banner → back to idle
  useEffect(() => {
    if (phase !== 'goal') return
    const t = setTimeout(() => setPhase('idle'), GOAL_MS)
    return () => clearTimeout(t)
  }, [phase])

  // clear the grass burst after it finishes
  useEffect(() => {
    if (!grass) return
    const t = setTimeout(() => setGrass(null), GRASS_MS)
    return () => clearTimeout(t)
  }, [grass])

  return (
    <>
      {(phase !== 'idle' || grass) && <style>{KEYFRAMES}</style>}
      {phase === 'ball' && <>
        <div ref={mountRef} style={{ position: 'fixed', inset: 0, zIndex: 99998, pointerEvents: 'none' }} aria-hidden="true" />
        <div ref={hitRef} onClick={() => ctlRef.current && ctlRef.current.kick()} aria-hidden="true"
          style={{ position: 'fixed', zIndex: 99999, transform: 'translate(-50%,-50%)', borderRadius: '50%', cursor: 'pointer', pointerEvents: 'auto' }} />
      </>}
      {grass && (
        <div key={grass.id} aria-hidden="true" style={{ position: 'fixed', left: grass.x, top: grass.y, zIndex: 99999, pointerEvents: 'none' }}>
          {Array.from({ length: 16 }).map((_, i) => {
            const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2.1   // mostly upward, fanned out
            const dist = 34 + Math.random() * 70
            const dx = Math.cos(ang) * dist, dy = Math.sin(ang) * dist
            const w = 3 + Math.random() * 3, h = 12 + Math.random() * 14
            const greens = ['#3aa84a', '#2f8f3e', '#57b85f', '#268a39']
            return (
              <span key={i} style={{
                position: 'absolute', left: 0, top: 0, width: w + 'px', height: h + 'px',
                background: greens[i % greens.length], borderRadius: '50% 50% 40% 40% / 70% 70% 30% 30%',
                transformOrigin: '50% 100%',
                ['--dx']: dx + 'px', ['--dy']: dy + 'px',
                ['--r0']: (Math.random() * 40 - 20) + 'deg', ['--r1']: (Math.random() * 160 - 80) + 'deg',
                animation: `ajGrass ${GRASS_MS}ms cubic-bezier(0.2,0.6,0.3,1) forwards`,
              }} />
            )
          })}
        </div>
      )}
      {phase === 'goal' && (
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'none', overflow: 'hidden' }}>
          <div style={{
            position: 'fixed', top: '50%', left: 0, whiteSpace: 'nowrap',
            fontFamily: '"Montserrat", sans-serif', fontWeight: 800, fontStyle: 'italic',
            fontSize: 'clamp(80px, 18vw, 240px)', letterSpacing: '0.02em',
            color: '#07272D', textShadow: '0 8px 28px rgba(7,39,45,0.35)',
            animation: 'ajGoalFly 2.8s cubic-bezier(0.25,0.1,0.25,1) forwards',
          }}>GOOOOOOOAAAAAAALLLLLLL</div>
        </div>
      )}
    </>
  )
}
